import { ReactNode, useId, useState } from 'react';

// Quiet table classes shared by the trends blocks (same look as the event tabs' tables).
export const tTable = 'w-full text-table tabular';
export const tHead = 'text-left text-small text-ink-2 border-b border-line-strong';
export const tTh = 'py-2 pr-3 last:pr-0 align-bottom font-semibold';
export const tTd = 'py-[11px] pr-3 last:pr-0 align-top text-ink';
export const tRow = 'border-b border-line hover:bg-selected';
export const tNum = 'text-right tabular';
export const tEm = 'block text-small text-ink-3';
export const dash = <span className="text-ink-3">–</span>;

const quietLink = 'text-accent underline underline-offset-[3px] hover:text-ink';

/**
 * One block of "Trends across events": a serif heading, then "CSV" (admins) and "How it's counted" as quiet links
 * in its head, the table, and a 13px footnote.
 */
export function Block({
  title,
  note,
  csv,
  footnote,
  children,
}: {
  title: string;
  /** The counting rule, shown under the head when "How it's counted" is opened. */
  note?: string;
  /** Admins only: one or more downloads ("CSV", "Hotel by night (CSV)"). */
  csv?: { label?: string; onClick: () => void }[];
  footnote?: ReactNode;
  children: ReactNode;
}) {
  const [how, setHow] = useState(false);
  const noteId = useId();
  const links: ReactNode[] = [
    ...(csv ?? []).map((c) => (
      <button key={c.label ?? 'csv'} type="button" className={quietLink} onClick={c.onClick}>
        {c.label ?? 'CSV'}
      </button>
    )),
    ...(note
      ? [
          <button
            key="how"
            type="button"
            className={quietLink}
            aria-expanded={how}
            aria-controls={noteId}
            onClick={() => setHow(!how)}
          >
            How it&rsquo;s counted
          </button>,
        ]
      : []),
  ];
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 mb-3">
        <h3 className="font-serif font-semibold text-[18px] leading-[26px] text-ink">{title}</h3>
        {links.length ? (
          <span className="text-[15px] leading-[22px] text-ink-3">
            {links.map((l, i) => (
              <span key={i}>
                {i ? ' · ' : ''}
                {l}
              </span>
            ))}
          </span>
        ) : null}
      </div>
      {note && how ? (
        <p id={noteId} className="text-small text-ink-2 mb-3 max-w-[80ch]">
          {note}
        </p>
      ) : null}
      {children}
      {footnote ? <div className="text-small text-ink-3 mt-2 max-w-[80ch]">{footnote}</div> : null}
    </div>
  );
}

/** A thin inline bar for a table row: teal for the main measure, a thinner line-strong bar under it for the comparison. */
export function InlineBar({ main, compare, max }: { main: number | null; compare?: number | null; max: number }) {
  const pct = (v: number) => `${Math.min(100, Math.max(0, (v / Math.max(1, max)) * 100))}%`;
  return (
    <div aria-hidden="true" className="pt-[6px]">
      {main ? <div className="h-[10px] rounded-[2px] bg-accent" style={{ width: pct(main) }} /> : null}
      {compare ? (
        <div className="h-[4px] rounded-[2px] bg-line-strong mt-[3px]" style={{ width: pct(compare) }} />
      ) : null}
    </div>
  );
}
